import type { ClientRef } from "../api/types.gen.ts";
import { type Key, t } from "../i18n/index.ts";

/** Our own tools, by the name they introduce themselves with (`Revisa-Client`). */
const OURS: Readonly<Record<string, Key>> = { "revisa-web": "client.web", "revisa-cli": "client.cli" };

/** Well-known MCP clients, by their `clientInfo.name`. */
const KNOWN: Readonly<Record<string, string>> = {
  "claude-code": "Claude Code",
  "claude-ai": "Claude",
  cursor: "Cursor",
  "cursor-vscode": "Cursor",
  "Visual Studio Code": "VS Code",
  codex: "Codex",
  "codex-mcp-client": "Codex",
  windsurf: "Windsurf",
};

const BROWSERS: readonly [RegExp, string][] = [
  [/Edg\//u, "Edge"],
  [/OPR\//u, "Opera"],
  [/Firefox\//u, "Firefox"],
  [/Chrome\//u, "Chrome"],
  [/Safari\//u, "Safari"],
];
const SYSTEMS: readonly [RegExp, string][] = [
  [/iPhone|iPad/u, "iOS"],
  [/Android/u, "Android"],
  [/Macintosh|Mac OS X/u, "macOS"],
  [/Windows/u, "Windows"],
  [/Linux/u, "Linux"],
];
const RAW_LENGTH = 60;

/** A tool's name for people: ours translated, well-known ones by their product name, others as they gave it. */
function nameOf(name: string): string {
  const ours = OURS[name];
  return ours ? t(ours) : (KNOWN[name] ?? name);
}

/** "Chrome on macOS" from a browser's User-Agent; null when it isn't one we know. */
function browserOf(ua: string): string | null {
  const browser = BROWSERS.find(([re]) => re.test(ua))?.[1];
  if (!browser) {
    return null;
  }
  const system = SYSTEMS.find(([re]) => re.test(ua))?.[1];
  return system ? t("client.browserOn", { browser, system }) : browser;
}

/** What a client is, for people: the tool and version, and the browser it ran in ("Web app · Firefox on macOS"). */
export function describeClient(c: Pick<ClientRef, "name" | "version" | "user_agent">): string {
  const name = c.name ? nameOf(c.name) : null;
  const tool = name && c.version ? `${name} ${c.version}` : name;
  const ua = c.user_agent ?? null;
  const browser = ua ? browserOf(ua) : null;
  const parts = [tool, browser ?? (tool || !ua ? null : ua.slice(0, RAW_LENGTH))].filter((p) => p !== null);
  return parts.length > 0 ? parts.join(" · ") : t("client.unknown");
}
